import { useQuery } from '@apollo/client'
import { usePrompt } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { DataTable, FAIcon, Input } from '@/components'
import { ALL_DEPARTMENTS_QUERY } from '@/modules/companies/queries'
import type {
  AllDepartmentsQuery,
  DepartmentNode,
} from '@/modules/companies/types.graphql'
import { useConfirm } from '@/utils/hooks'
import { OldAddButton, Header, OldSection, SectionHeader } from '../common'
import type { DepartmentForm } from './DepartmentPrompt'
import { DepartmentPrompt } from './DepartmentPrompt'
import { useDepartmentMutations } from './hooks'
import { createColumnHelper } from '@tanstack/react-table'
import { useCallback, useMemo, useState } from 'react'

const column = createColumnHelper<DepartmentNode>()

export const DepartmentSettings: React.JSX.CFC = () => {
  const translations = useTranslate({
    prompt: {
      delete: ['settings.prompts.delete', { name: '' }],
      deleteTitle: 'settings.prompts.department-delete-title',
    },
    externalId: 'common.id',
    departments: 'common.departments',
    name: 'common.name',
    noResults: 'common.no-results',
    search: 'common.search',
  })

  const addPrompt = usePrompt()
  const confirm = useConfirm()
  const mutations = useDepartmentMutations()

  const { data } = useQuery<AllDepartmentsQuery, {}>(ALL_DEPARTMENTS_QUERY)

  const departments = useMemo(
    () => data?.allDepartments.edges?.map(edge => edge.node) ?? [],
    [data]
  )
  const [search, setSearch] = useState('')

  const filteredDepartments = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase()
    if (!normalizedSearch) return departments

    return departments.filter(department =>
      [department.name, department.externalId]
        .filter(Boolean)
        .some(value => value.toLowerCase().includes(normalizedSearch))
    )
  }, [departments, search])

  async function handleCreateDepartment() {
    const { data } = await addPrompt<DepartmentForm | null>(resolve => (
      <DepartmentPrompt existingDepartments={departments} onSubmit={resolve} />
    ))
    if (data === null) return

    void mutations.createDepartment({
      variables: {
        input: data,
      },
    })
  }

  const handlePatchDepartment = useCallback(
    async (department: DepartmentNode) => {
      const { data } = await addPrompt<DepartmentForm | null>(resolve => (
        <DepartmentPrompt
          initialData={department}
          existingDepartments={departments}
          onSubmit={resolve}
        />
      ))
      if (data === null) return

      void mutations.patchDepartment({
        variables: {
          id: department.id,
          input: {
            externalId: data.externalId,
            name: data.name,
          },
        },
      })
    },
    [addPrompt, departments, mutations]
  )

  const handleDeleteDepartment = useCallback(
    async (department: DepartmentNode) => {
      const { data: answer } = await confirm(
        translations.prompt.delete({ name: department.name }),
        translations.prompt.deleteTitle
      )
      if (!answer) return

      void mutations.deleteDepartment({
        variables: {
          id: department.id,
        },
      })
    },
    [confirm, mutations, translations.prompt]
  )

  const tableColumns = useMemo(
    () => [
      column.accessor('name', {
        header: translations.name,
        enableSorting: true,
      }),
      column.accessor('externalId', {
        header: translations.externalId,
        enableSorting: true,
        cell(info) {
          return <span>{info.getValue() ?? '-'}</span>
        },
      }),
      column.display({
        id: 'edit',
        meta: {
          narrow: true,
        },
        cell(info) {
          const department = info.row.original

          return (
            <FAIcon
              className="text-gray-400 hover:text-secondary"
              icon="edit"
              cursor="pointer"
              onClick={() => handlePatchDepartment(department)}
            />
          )
        },
      }),
      column.display({
        id: 'delete',
        meta: {
          narrow: true,
        },
        cell(info) {
          const department = info.row.original

          return (
            <FAIcon
              className="text-gray-400 hover:text-red-500"
              icon="times"
              cursor="pointer"
              onClick={() => handleDeleteDepartment(department)}
            />
          )
        },
      }),
    ],
    [
      handleDeleteDepartment,
      handlePatchDepartment,
      translations.externalId,
      translations.name,
    ]
  )

  return (
    <OldSection>
      <Header>
        <SectionHeader>{translations.departments}</SectionHeader>
        <OldAddButton adding={false} onClick={handleCreateDepartment} />
      </Header>

      <div className="mb-4 max-w-sm">
        <Input
          aria-label={translations.search}
          placeholder={translations.search}
          type="search"
          value={search}
          onChange={setSearch}
        />
      </div>

      <DataTable
        columns={tableColumns}
        data={filteredDepartments}
        noDataText={translations.noResults}
        pagination={{ pageSize: 20 }}
      />
    </OldSection>
  )
}
